import React, { useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import {
  validateSettings,
  settingsPatch,
  settingsSignature
} from '../lib/validateSettings';
import { settingsForMode } from '../lib/settingsMode';
import {
  dateTime,
  LoadError,
  Loading,
  RefreshButton
} from '../components/Common';
import HelpTip from '../components/HelpTip';
import StoreChecklist from '../components/StoreChecklist';
import SettingsConfirm from '../components/SettingsConfirm';
import ModeSelector from '../components/settings/ModeSelector';
import RatingOptions from '../components/settings/RatingOptions';
import AllowedServices from '../components/settings/AllowedServices';
import SettingsDetails from '../components/settings/SettingsDetails';

const SettingsTab = ({ data, latest, canEdit, onDirtyChange, storesData }) => {
  const { settings, loading, error, saving, saveSettings, exists, refresh } =
    data;
  const [draft, setDraft] = useState(settings);
  const [baseline, setBaseline] = useState(settings);
  const [pending, setPending] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const dirty = Boolean(
    draft &&
      baseline &&
      settingsSignature(draft) !== settingsSignature(baseline)
  );
  useEffect(() => {
    if (!dirty && settings) {
      setDraft(settings);
      setBaseline(settings);
    }
  }, [settings, dirty]);
  useEffect(() => {
    onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);
  useEffect(() => {
    refresh();
  }, [refresh]);
  if (!draft)
    return (
      <>
        <LoadError error={error} />
        {!error && <Loading label="Loading settings…" />}
      </>
    );
  const disabled = !canEdit || saving || loading;
  const errors = validateSettings(draft);
  const update = patch => setDraft(prev => ({ ...prev, ...patch }));
  const selectMode = mode => {
    if (disabled) return;
    if (mode === 'live') setPending({ mode });
    else update(settingsForMode(mode, draft));
  };
  const save = async e => {
    e.preventDefault();
    if (disabled || errors.length) return;
    setSaveError(null);
    try {
      const saved = await saveSettings(settingsPatch(draft));
      const next = saved || { ...draft, ...settingsPatch(draft) };
      setDraft(next);
      setBaseline(next);
      toast.success('Rate Shop settings saved');
    } catch (err) {
      setSaveError(err);
      toast.error(err.message);
    }
  };
  return (
    <>
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">
            Settings {dirty && <Badge bg="warning">Unsaved changes</Badge>}
          </h5>
          <RefreshButton onClick={refresh} loading={loading || saving} />
        </Card.Header>
        <Card.Body>
          <LoadError error={error || saveError} />
          {!exists && (
            <Alert variant="info">
              Recommended settings are shown. Save to start using them.
            </Alert>
          )}
          <Form onSubmit={save}>
            <ModeSelector
              draft={draft}
              disabled={disabled}
              onChange={selectMode}
            />
            <div className="mb-4">
              <h5>
                Stores{' '}
                <HelpTip label="Stores">
                  Only orders from ticked ShipStation stores are checked. Keep
                  at least one selected: an empty saved list would include every
                  store.
                </HelpTip>
              </h5>
              <p className="small text-muted">
                Choose which stores Rate Shop should look after.
              </p>
              <LoadError error={storesData.error} />
              {storesData.loading && (
                <Loading label="Loading ShipStation stores…" />
              )}
              <StoreChecklist
                stores={storesData.stores}
                value={draft.storesInScope}
                disabled={disabled || storesData.loading}
                onChange={storesInScope => update({ storesInScope })}
              />
              <p
                className={`small mt-2 ${
                  !draft.storesInScope.length
                    ? 'text-danger fw-bold'
                    : 'text-muted'
                }`}
              >
                Keep at least one store selected. An empty list would mean every
                store, so it cannot be saved.
              </p>
            </div>
            <RatingOptions draft={draft} update={update} disabled={disabled} />
            <AllowedServices
              draft={draft}
              update={update}
              disabled={disabled}
            />
            <SettingsDetails
              draft={draft}
              update={update}
              disabled={disabled}
            />
            {errors.length > 0 && (
              <Alert variant="warning">
                <strong>Before saving:</strong>
                <ul className="mb-0">
                  {errors.map(message => (
                    <li key={message}>{message}</li>
                  ))}
                </ul>
              </Alert>
            )}
            <div className="border-top pt-3 d-flex flex-wrap justify-content-between gap-3 align-items-center">
              <div className="small text-muted">
                Updated {dateTime(settings?.updatedAt)}
                <br />
                By{' '}
                {settings?.updatedBy === 'seed'
                  ? 'backend seed'
                  : settings?.updatedBy || '—'}
              </div>
              {canEdit && (
                <Button type="submit" disabled={disabled || errors.length > 0}>
                  {saving ? 'Saving…' : 'Save settings'}
                </Button>
              )}
            </div>
          </Form>
        </Card.Body>
      </Card>
      {pending && (
        <SettingsConfirm
          pending={pending}
          draft={draft}
          savedStores={settings.storesInScope}
          latest={latest}
          onHide={() => setPending(null)}
          onConfirm={() => {
            if (canEdit) update(settingsForMode(pending.mode, draft));
            setPending(null);
          }}
        />
      )}
    </>
  );
};
export default SettingsTab;
